import { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// Styles
import "./styles/markdown.css";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EnrollmentProvider } from "./context/EnrollmentContext";
import { CartProvider } from "./context/CartContext";

// Components - Keep these eagerly loaded (used on every page)
import Navbar from "./components/layout/Navbar";
import AppFooter from "./components/layout/AppFooter";
import AuthGuard from "./components/auth/AuthGuard";
import RoleGuard from "./components/auth/RoleGuard";
import RedirectToCourseEnroll from "./components/RedirectToCourseEnroll";
import MaintenanceCheck from "./components/MaintenanceCheck";
import ScrollToTop from "./components/ScrollToTop";

// Loading Spinner for Suspense fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// ==================== LAZY LOADED PAGES ====================

// Public Pages
const Home = lazy(() => import("./pages/Home"));
const Courses = lazy(() => import("./pages/Courses"));
const CourseEnrollment = lazy(() => import("./pages/CourseEnrollment"));
const CourseDetailView = lazy(() => import("./pages/CourseDetailView"));
const Cart = lazy(() => import("./pages/Cart"));
const DirectPayment = lazy(() => import("./pages/DirectPayment"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const About = lazy(() => import("./pages/About"));
const ContactPage = lazy(() => import("./pages/Contact"));
const Features = lazy(() => import("./pages/Features"));

// Legal Pages
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./pages/legal/CookiesPolicy"));
const Accessibility = lazy(() => import("./pages/legal/Accessibility"));
const Impartiality = lazy(() => import("./pages/legal/Impartiality"));

// Auth Pages
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));

// Admin/Creator Pages

const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCourses = lazy(() => import("./pages/admin/AdminCourses"));
const AdminQuestions = lazy(() => import("./pages/admin/AdminQuestions"));
const PipelineDashboard = lazy(() => import("./pages/admin/PipelineDashboard"));
const CourseEditor = lazy(() => import("./pages/admin/CourseEditor"));
const ChangesRequestedPage = lazy(() => import("./pages/admin/ChangesRequestedPage"));
const InProgressCoursesPage = lazy(() => import("./pages/admin/InProgressCoursesPage"));
const CompletedCoursesPage = lazy(() => import("./pages/admin/CompletedCoursesPage"));

const RejectedCoursesPage = lazy(() => import("./pages/admin/RejectedCoursesPage"));
const ViewProgress = lazy(() => import("./pages/admin/ViewProgress"));
const MockTests = lazy(() => import("./pages/candidate/MockTests"));
// Shared role shell (sidebar + content) used by admin, creator, reviewer, approver
const RoleLayout = lazy(() => import("./components/layout/RoleLayout"));

// Super Admin Pages
const SuperAdminDashboard = lazy(() => import("./pages/super-admin/SuperAdminDashboard"));
const UserManagement = lazy(() => import("./pages/super-admin/UserManagement"));
const UserDetail = lazy(() => import("./pages/super-admin/UserDetail"));
const UniversityManagement = lazy(() => import("./pages/super-admin/UniversityManagement"));
const UniversityDetail = lazy(() => import("./pages/super-admin/UniversityDetail"));
const SystemSettings = lazy(() => import("./pages/super-admin/SystemSettings"));
const SuperAdminCourses = lazy(() => import("./pages/super-admin/SuperAdminCourses"));
const AnalyticsDashboard = lazy(() => import("./components/analytics/AnalyticsDashboard"));
const CourseAnalyticsDetail = lazy(() => import("./pages/super-admin/CourseAnalyticsDetail"));
const CouponManagement = lazy(() => import("./components/analytics/CouponManagement"));
const CouponDetail = lazy(() => import("./pages/super-admin/CouponDetail"));

// Reviewer Pages
const ReviewerDashboard = lazy(() => import("./pages/reviewer/ReviewerDashboard"));
const CourseReviewList = lazy(() => import("./pages/reviewer/CourseReviewList"));
const CourseReviewDetail = lazy(() => import("./pages/reviewer/CourseReviewDetail"));
const RejectedCoursesList = lazy(() => import("./pages/reviewer/RejectedCoursesList"));
const RequestedChangesList = lazy(() => import("./pages/reviewer/RequestedChangesList"));

// Approver Pages
const ApproverDashboard = lazy(() => import("./context/approver/ApproverDashboard"));
const CourseApprovalDetail = lazy(() => import("./context/approver/CourseApprovalDetail"));
const ApproverCourseList = lazy(() => import("./context/approver/ApproverCourseList"));

// Candidate Pages
const CandidateDashboard = lazy(() => import("./pages/candidate/CandidateDashboard"));
const AccountPage = lazy(() => import("./pages/candidate/AccountPage"));
const AdminAccountPage = lazy(() => import("./pages/super-admin/AdminAccountPage"));

const AppContent = () => {
  const { user } = useAuth();
  const roleKey = user?.active_role || user?.role || "guest";

  return (
    <Router>
      <MaintenanceCheck>
        <div className="min-h-screen relative bg-gray-50 flex flex-col">
          <ScrollToTop />
          <Navbar />

          <main className="flex-1">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Home />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/courses/:id/enroll" element={<CourseEnrollment />} />
                <Route path="/courses/:id/details" element={<CourseDetailView />} />
                <Route
                  path="/courses/:id/mock-tests"
                  element={
                    <AuthGuard>
                      <MockTests />
                    </AuthGuard>
                  }
                />
                {/* Redirect old course URLs */}
                <Route path="/courses/:id" element={<RedirectToCourseEnroll />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/payment/:courseId" element={<DirectPayment />} />
                <Route
                  path="/payment-success"
                  element={
                    <AuthGuard>
                      <PaymentSuccess />
                    </AuthGuard>
                  }
                />
                <Route
                  path="/wishlist"
                  element={
                    <AuthGuard>
                      <Wishlist />
                    </AuthGuard>
                  }
                />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/features" element={<Features />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/cookies-policy" element={<CookiesPolicy />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/impartiality" element={<Impartiality />} />

                {/* Auth Routes */}
                <Route path="/auth/login" element={<Login />} />
                <Route path="/auth/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                {/* Protected Routes - Require Authentication */}
                <Route
                  path="/dashboard"
                  element={
                    <AuthGuard>
                      <CandidateDashboard key={`dashboard-${roleKey}`} />
                    </AuthGuard>
                  }
                />

                <Route
                  path="/account"
                  element={
                    <AuthGuard>
                      <AccountPage />
                    </AuthGuard>
                  }
                />

                {/* Creator Routes - Previously Admin, now Creator Role.
                    All wrapped in RoleLayout so the sidebar persists across pages. */}
                <Route
                  element={
                    <RoleGuard requiredRole="creator">
                      <RoleLayout role="creator" />
                    </RoleGuard>
                  }
                >
                  <Route path="/admin" element={<AdminDashboard key={`admin-${roleKey}`} />} />
                  <Route path="/admin/courses" element={<AdminCourses />} />
                  <Route path="/admin/changes-requested" element={<ChangesRequestedPage />} />
                  <Route path="/admin/in-progress" element={<InProgressCoursesPage />} />
                  <Route path="/admin/completed" element={<CompletedCoursesPage />} />
                  <Route path="/admin/rejected" element={<RejectedCoursesPage />} />
                  <Route path="/admin/courses/:courseId/edit" element={<CourseEditor />} />
                  <Route path="/admin/questions" element={<AdminQuestions />} />
                  <Route path="/admin/pipeline" element={<PipelineDashboard />} />
                  <Route path="/admin/viewprogress" element={<ViewProgress />} />
                </Route>
                {/* Admin Routes - Previously Super Admin, now Admin Role.
                    All wrapped in RoleLayout so the sidebar persists across pages. */}
                <Route
                  element={
                    <RoleGuard requiredRole="admin">
                      <RoleLayout role="admin" />
                    </RoleGuard>
                  }
                >
                  <Route
                    path="/super-admin"
                    element={<SuperAdminDashboard key={`super-admin-${roleKey}`} />}
                  />
                  <Route
                    path="/super-admin/dashboard"
                    element={<SuperAdminDashboard key={`super-admin-dash-${roleKey}`} />}
                  />
                  <Route path="/super-admin/users" element={<UserManagement />} />
          <Route path="/super-admin/users/:userId" element={<UserDetail />} />
                  <Route
                    path="/super-admin/universities"
                    element={<UniversityManagement />}
                  />
                  <Route
                    path="/super-admin/universities/:universityId"
                    element={<UniversityDetail />}
                  />
                  <Route path="/super-admin/settings" element={<SystemSettings />} />
                  <Route path="/super-admin/courses" element={<SuperAdminCourses />} />
                  <Route path="/super-admin/analytics" element={<AnalyticsDashboard />} />
                  <Route
                    path="/super-admin/analytics/course/:courseId"
                    element={<CourseAnalyticsDetail />}
                  />
                  <Route path="/super-admin/coupons" element={<CouponManagement />} />
                  <Route path="/super-admin/coupons/:couponId" element={<CouponDetail />} />
                  <Route path="/super-admin/account" element={<AdminAccountPage />} />
                </Route>

                {/* Reviewer Routes - Require Reviewer Role.
                    All wrapped in RoleLayout so the sidebar persists across pages. */}
                <Route
                  element={
                    <RoleGuard requiredRole="reviewer">
                      <RoleLayout role="reviewer" />
                    </RoleGuard>
                  }
                >
                  <Route path="/reviewer" element={<ReviewerDashboard key={`reviewer-${roleKey}`} />} />
                  <Route
                    path="/reviewer/dashboard"
                    element={<ReviewerDashboard key={`reviewer-dash-${roleKey}`} />}
                  />
                  <Route path="/reviewer/courses" element={<CourseReviewList />} />
                  <Route path="/reviewer/courses/rejected" element={<RejectedCoursesList />} />
                  <Route
                    path="/reviewer/courses/changes-requested"
                    element={<RequestedChangesList />}
                  />
                  <Route path="/reviewer/courses/:courseId" element={<CourseReviewDetail />} />
                </Route>
                {/* Approver Routes - Require Approver Role.
                    All wrapped in RoleLayout so the sidebar persists across pages. */}
                <Route
                  element={
                    <RoleGuard requiredRole="approver">
                      <RoleLayout role="approver" />
                    </RoleGuard>
                  }
                >
                  <Route path="/approver" element={<ApproverDashboard key={`approver-${roleKey}`} />} />
                  <Route
                    path="/approver/dashboard"
                    element={<ApproverDashboard key={`approver-dash-${roleKey}`} />}
                  />
                  <Route path="/approver/pending" element={<ApproverCourseList view="pending" />} />
                  <Route path="/approver/approved" element={<ApproverCourseList view="approved" />} />
                  <Route path="/approver/revising" element={<ApproverCourseList view="revising" />} />
                  <Route path="/approver/live" element={<ApproverCourseList view="live" />} />
                  <Route path="/approver/courses/:courseId" element={<CourseApprovalDetail />} />
                </Route>
              </Routes>
            </Suspense>
          </main>

          <AppFooter />

          {/* Toast Notifications */}
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: "#363636",
                color: "#fff",
                padding: "16px 24px",
                borderRadius: "8px",
                textAlign: "center"
              },
              success: {
                style: {
                  background: "#43B14B",
                },
              },
              error: {
                style: {
                  background: "#ef4444",
                },
              },
            }}
          />
        </div>
      </MaintenanceCheck>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <EnrollmentProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </EnrollmentProvider>
    </AuthProvider>
  );
}

export default App;
